import { v4 as uuidv4 } from 'uuid';

// ─── Seed Research Data ─────────────────────────────────────────
const SEED_RESEARCH = [
  {
    id: uuidv4(),
    title: 'Replacing Zapier with Autonomous AI Agents: A Practical Framework',
    authors: ['D. Mitchell', 'R. Vasquez'],
    abstract:
      'We present an architecture for replacing traditional workflow automation platforms with LLM-driven autonomous agents. Our system reduces integration setup time by 74% while maintaining equivalent reliability across 12 enterprise benchmarks. We evaluate agent orchestration strategies across multi-step workflows involving CRM, email, and document processing pipelines.',
    category: 'AI Automation Systems',
    tags: ['agents', 'workflow automation', 'LLM', 'enterprise'],
    publishedDate: '2026-02-10',
    status: 'published',
    citedBy: 42,
    pdfUrl: '#',
    venue: 'Vector Labs Reports',
  },
  {
    id: uuidv4(),
    title: 'Local LLM Deployment for SMBs: Cost, Latency, and Privacy Trade-offs',
    authors: ['R. Vasquez', 'K. Chen'],
    abstract:
      'Small and medium businesses increasingly require AI capabilities without cloud dependency. We benchmark seven open-weight models across consumer-grade hardware configurations, measuring inference latency, memory utilization, and task accuracy. Our recommended stack achieves GPT-4-level performance on domain-specific tasks at 1/20th the operational cost.',
    category: 'Local AI Infrastructure',
    tags: ['local AI', 'deployment', 'SMB', 'benchmarks', 'cost analysis'],
    publishedDate: '2026-01-28',
    status: 'published',
    citedBy: 87,
    pdfUrl: '#',
    venue: 'Vector Labs Reports',
  },
  {
    id: uuidv4(),
    title: 'Building Document Intelligence Systems with Retrieval-Augmented Generation',
    authors: ['K. Chen', 'D. Mitchell', 'A. Petrov'],
    abstract:
      'We describe the design and deployment of a production document intelligence system combining OCR, embedding-based retrieval, and instruction-tuned language models. The system processes 50,000+ documents monthly for a legal services client with 96.3% extraction accuracy. We detail our chunking strategies, re-ranking pipeline, and hallucination mitigation techniques.',
    category: 'Data Pipelines',
    tags: ['RAG', 'document processing', 'information extraction', 'production systems'],
    publishedDate: '2026-01-15',
    status: 'published',
    citedBy: 134,
    pdfUrl: '#',
    venue: 'Vector Labs Reports',
  },
  {
    id: uuidv4(),
    title: 'How Small Engineering Teams Beat Enterprise Software: A Case Study',
    authors: ['D. Mitchell'],
    abstract:
      'We present three case studies where 2–4 person engineering teams at Vector Labs delivered production AI systems in 4–8 weeks that outperformed solutions from established enterprise vendors. We analyze the organizational, architectural, and tooling decisions that enable this velocity, and propose a framework for rapid AI systems deployment.',
    category: 'Developer Productivity',
    tags: ['case study', 'rapid engineering', 'team structure', 'consulting'],
    publishedDate: '2025-12-20',
    status: 'published',
    citedBy: 56,
    pdfUrl: '#',
    venue: 'Vector Labs Reports',
  },
  {
    id: uuidv4(),
    title: 'Agent Workflow Orchestration: Patterns for Multi-Step LLM Pipelines',
    authors: ['A. Petrov', 'R. Vasquez'],
    abstract:
      'As LLM-based agents become integral to automation workflows, orchestrating multi-step pipelines introduces challenges in error handling, state management, and observability. We catalog 8 orchestration patterns observed across 20+ production deployments and evaluate their trade-offs in reliability, debuggability, and latency. We release VectorFlow, an open-source framework implementing these patterns.',
    category: 'Agent Workflows',
    tags: ['agents', 'orchestration', 'VectorFlow', 'open source', 'patterns'],
    publishedDate: '2025-12-05',
    status: 'published',
    citedBy: 203,
    pdfUrl: '#',
    venue: 'Vector Labs Reports',
  },
  {
    id: uuidv4(),
    title: 'Internal Copilots: Deploying Custom AI Assistants for Enterprise Teams',
    authors: ['K. Chen', 'D. Mitchell'],
    abstract:
      'We describe our methodology for building and deploying custom AI copilots tailored to specific organizational workflows. Spanning customer support, engineering, and operations use cases, we measure productivity improvements averaging 35% across six enterprise deployments. We discuss fine-tuning strategies, retrieval integration, and the human feedback loops necessary for continuous improvement.',
    category: 'AI Automation Systems',
    tags: ['copilots', 'enterprise AI', 'fine-tuning', 'productivity'],
    publishedDate: '2025-11-18',
    status: 'published',
    citedBy: 91,
    pdfUrl: '#',
    venue: 'Vector Labs Reports',
  },
  {
    id: uuidv4(),
    title: 'VectorFlow Architecture: Design Decisions for an AI Workflow Engine',
    authors: ['R. Vasquez', 'A. Petrov', 'K. Chen'],
    abstract:
      'Technical deep-dive into the architecture of VectorFlow, our open-source AI workflow engine. We cover the event-driven execution model, plugin system for LLM providers, deterministic replay for debugging, and the observability layer. Benchmarks show VectorFlow handles 10,000 concurrent workflows with sub-100ms scheduling latency.',
    category: 'Agent Workflows',
    tags: ['VectorFlow', 'architecture', 'open source', 'workflow engine'],
    publishedDate: '2025-11-01',
    status: 'published',
    citedBy: 167,
    pdfUrl: '#',
    venue: 'Vector Labs Reports',
  },
  {
    id: uuidv4(),
    title: 'Benchmarking Embedding Models for Domain-Specific Retrieval',
    authors: ['A. Petrov'],
    abstract:
      'We evaluate 14 embedding models across 5 domain-specific retrieval tasks: legal document search, medical literature, financial reports, software documentation, and customer support tickets. Results show that domain-adapted models outperform general-purpose embeddings by 12–28% on nDCG@10, with significant variance across domains.',
    category: 'Data Pipelines',
    tags: ['embeddings', 'retrieval', 'benchmarks', 'domain adaptation'],
    publishedDate: '2025-10-15',
    status: 'published',
    citedBy: 245,
    pdfUrl: '#',
    venue: 'Vector Labs Reports',
  },
  {
    id: uuidv4(),
    title: 'CRM Automation with LLMs: Eliminating Manual Data Entry',
    authors: ['D. Mitchell', 'K. Chen'],
    abstract:
      'Draft paper exploring the use of language models for automated CRM data extraction and entry. Preliminary results from a pilot deployment show 89% reduction in manual data entry time with 97.1% accuracy on structured field extraction from emails, call transcripts, and meeting notes.',
    category: 'AI Automation Systems',
    tags: ['CRM', 'automation', 'data extraction', 'enterprise'],
    publishedDate: null,
    status: 'draft',
    citedBy: 0,
    pdfUrl: null,
    venue: null,
  },
];

// ─── Categories ─────────────────────────────────────────────────
export const CATEGORIES = [
  'AI Automation Systems',
  'Agent Workflows',
  'Developer Productivity',
  'Local AI Infrastructure',
  'Data Pipelines',
];

// ─── LocalStorage-backed store ──────────────────────────────────
const STORAGE_KEY = 'fvl_research';

function getStoredResearch() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // corrupted — reset
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED_RESEARCH));
  return SEED_RESEARCH;
}

function persist(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

// ─── CRUD Operations (mock API) ─────────────────────────────────
export function getAllResearch() {
  return getStoredResearch();
}

export function getPublishedResearch() {
  return getStoredResearch().filter((r) => r.status === 'published');
}

export function getResearchById(id) {
  return getStoredResearch().find((r) => r.id === id) || null;
}

export function getResearchByCategory(category) {
  return getPublishedResearch().filter((r) => r.category === category);
}

export function searchResearch(query) {
  const q = query.toLowerCase();
  return getPublishedResearch().filter(
    (r) =>
      r.title.toLowerCase().includes(q) ||
      r.abstract.toLowerCase().includes(q) ||
      r.authors.some((a) => a.toLowerCase().includes(q)) ||
      r.tags.some((t) => t.toLowerCase().includes(q))
  );
}

export function createResearch(data) {
  const items = getStoredResearch();
  const newItem = {
    id: uuidv4(),
    ...data,
    citedBy: 0,
    publishedDate: data.status === 'published' ? new Date().toISOString().split('T')[0] : null,
  };
  items.unshift(newItem);
  persist(items);
  return newItem;
}

export function updateResearch(id, updates) {
  const items = getStoredResearch();
  const idx = items.findIndex((r) => r.id === id);
  if (idx === -1) return null;
  if (updates.status === 'published' && !items[idx].publishedDate) {
    updates.publishedDate = new Date().toISOString().split('T')[0];
  }
  items[idx] = { ...items[idx], ...updates };
  persist(items);
  return items[idx];
}

export function deleteResearch(id) {
  const items = getStoredResearch().filter((r) => r.id !== id);
  persist(items);
  return true;
}

export function resetToSeed() {
  persist(SEED_RESEARCH);
  return SEED_RESEARCH;
}
